// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file DateTimeInput.tsx
 * @input Uses React, Field, Calendar, usePopover, time parsing utilities
 * @output Exports DateTimeInput component, DateTimeInputProps
 * @position Core implementation; consumed by index.ts, tested by DateTimeInput.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/DateTimeInput/DateTimeInput.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/DateTimeInput/DateTimeInput.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/DateTimeInput/index.ts (exports if types change)
 * - /apps/storybook/stories/DateTimeInput.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/DateTimeInput/ (showcase blocks)
 */
import { useId, useState, useCallback, useRef, useMemo, useOptimistic, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, radiusVars, spacingVars, typographyVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { Field, inputWrapperStyles, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputStatusFocusWithinStyles } from "../Field/index.js";
import { Icon } from "../Icon/index.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { Spinner } from "../Spinner/index.js";
import { Calendar } from "../Calendar/index.js";
import { useCalendarConstraints } from "../Calendar/hooks/index.js";
import { usePopover } from "../Popover/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { useInputContainer } from "../hooks/useInputContainer.js";
import { useInputStatusIcon } from "../hooks/useInputStatusIcon.js";
import { parseDateInput, parseTimeInput, formatDisplayTime12h, formatDisplayTime24h, formatISOTime, isTimeInRange, adjustTime, mergeProps, mergeRefs } from "../utils/index.js";
import { plainDateFromISO, plainDateToISO, plainDateFormat, DATE_FORMAT_LONG } from "../utils/plainDate.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

/** Supported minute increments for arrow-key stepping of the time field. */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  row: {
    k1xSpc: "x78zum5",
    kOIVth: "x1txdalj",
    $$css: true
  },
  dateWrapper: {
    kUk6DE: "x98rzlu",
    kCS8Yb: "x1r8uery",
    $$css: true
  },
  timeWrapper: {
    kUk6DE: "x98rzlu",
    kCS8Yb: "x1r8uery",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};
function splitDateTime(dt) {
  if (!dt) {
    return {
      date: undefined,
      time: undefined
    };
  }
  const tIndex = dt.indexOf('T');
  if (tIndex === -1) {
    return {
      date: dt,
      time: undefined
    };
  }
  return {
    date: dt.slice(0, tIndex),
    time: dt.slice(tIndex + 1)
  };
}
function combineDateTime(date, time) {
  if (!date || !time) {
    return undefined;
  }
  return `${date}T${time}`;
}
function getDefaultTime(hasSeconds) {
  const now = new Date();
  return formatISOTime({
    hour: now.getHours(),
    minute: now.getMinutes(),
    second: now.getSeconds()
  }, hasSeconds);
}

/**
 * A combined date and time picker with side-by-side date input and
 * time input under a single label. The date input opens a calendar
 * popover; the time input supports typed entry and arrow-key adjustment.
 *
 * @example
 * ```
 * <DateTimeInput
 *   label="Meeting time"
 *   value={dateTime}
 *   onChange={setDateTime}
 * />
 * ```
 */
export function DateTimeInput({
  label,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  disabledMessage,
  value,
  onChange,
  changeAction,
  isLoading = false,
  min,
  max,
  dateConstraints,
  hasSeconds = false,
  hourFormat = '12h',
  timeIncrement = 1,
  hasClear = false,
  placeholder: placeholderFromProps,
  timePlaceholder: timePlaceholderFromProps,
  timeLabel,
  size: sizeProp,
  status,
  labelTooltip,
  numberOfMonths = 1,
  weekStartsOn,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const placeholder = placeholderFromProps ?? t('@astryx.dateTimeInput.placeholder');
  const timePlaceholder = timePlaceholderFromProps ?? t('@astryx.dateTimeInput.timePlaceholder');
  const size = useSize(sizeProp, 'md');
  const dateInputId = useId();
  const timeInputId = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  const timeContainerRef = useRef(null);
  const calendarRef = useRef(null);
  const lastFiredDateRef = useRef(undefined);
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;
  const isEffectivelyDisabled = isDisabled || isBusy;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the outer row container (which already exists)
  // and both the date and time inputs stay perceivable via aria-disabled
  // instead of the disabled attribute. Typing is blocked with readOnly and the
  // value mutation guards; calendar activation is blocked by the
  // isEffectivelyDisabled guards. Only the persistent isDisabled state (not the
  // transient busy state) surfaces a reason.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the inputs, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });

  // DateTimeInput fixes its status presentation to the detached message box,
  // so the shared helper suppresses the on-field icon (the message box carries
  // its own leading glyph). Routed through the helper for consistency with the
  // rest of the bordered input family.
  const {
    statusIcon,
    describedBy: statusTooltipDescribedBy
  } = useInputStatusIcon({
    status,
    statusVariant: 'detached'
  });
  const ariaDescribedBy = [description ? descriptionID : null, status?.message ? statusMessageID : null, statusTooltipDescribedBy, showsDisabledMessage ? disabledMessageTooltip.describedBy : null].filter(Boolean).join(' ') || undefined;

  // Split min/max and current value
  const minParts = useMemo(() => splitDateTime(min), [min]);
  const maxParts = useMemo(() => splitDateTime(max), [max]);
  const valueParts = useMemo(() => splitDateTime(optimisticValue), [optimisticValue]);

  // Date constraints from min/max
  const calendarMin = minParts.date;
  const calendarMax = maxParts.date;
  const {
    isDateDisabled
  } = useCalendarConstraints({
    min: calendarMin,
    max: calendarMax,
    dateConstraints
  });

  // Time constraints change depending on selected date
  const timeMin = useMemo(() => {
    if (!minParts.date || !minParts.time || !valueParts.date) {
      return undefined;
    }
    return valueParts.date === minParts.date ? minParts.time : undefined;
  }, [minParts.date, minParts.time, valueParts.date]);
  const timeMax = useMemo(() => {
    if (!maxParts.date || !maxParts.time || !valueParts.date) {
      return undefined;
    }
    return valueParts.date === maxParts.date ? maxParts.time : undefined;
  }, [maxParts.date, maxParts.time, valueParts.date]);

  // --- Date input state ---
  const [datePendingInput, setDatePendingInput] = useState(null);
  const prevDateRef = useRef(valueParts.date);
  if (valueParts.date !== prevDateRef.current) {
    prevDateRef.current = valueParts.date;
    if (valueParts.date !== lastFiredDateRef.current) {
      lastFiredDateRef.current = undefined;
      if (datePendingInput !== null) {
        setDatePendingInput(null);
      }
    }
  }
  const dateDisplayValue = datePendingInput !== null ? datePendingInput : valueParts.date && /^\d{4}-\d{2}-\d{2}$/.test(valueParts.date) ? plainDateFormat(plainDateFromISO(valueParts.date), DATE_FORMAT_LONG) : '';
  const isDateInputValid = datePendingInput === null || !datePendingInput.trim() ? true : parseDateInput(datePendingInput) !== null;

  // --- Time input state ---
  const [timePendingInput, setTimePendingInput] = useState(null);
  const [isTimeFocused, setIsTimeFocused] = useState(false);
  const formatDisplayTime = hourFormat === '12h' ? formatDisplayTime12h : formatDisplayTime24h;
  const timeDisplayValue = useMemo(() => {
    if (timePendingInput !== null) {
      return timePendingInput;
    }
    return valueParts.time ? formatDisplayTime(valueParts.time, hasSeconds) : '';
  }, [timePendingInput, valueParts.time, formatDisplayTime, hasSeconds]);
  const isTimeInputValid = useMemo(() => {
    if (timePendingInput === null || !timePendingInput.trim()) {
      return true;
    }
    const parsed = parseTimeInput(timePendingInput, hasSeconds);
    if (!parsed) {
      return false;
    }
    return isTimeInRange(parsed, timeMin, timeMax);
  }, [timePendingInput, hasSeconds, timeMin, timeMax]);
  const resolvedTimePlaceholder = useMemo(() => {
    if (isTimeFocused && !timeDisplayValue) {
      return hourFormat === '12h' ? 'e.g., 2:30 PM' : 'e.g., 14:30';
    }
    return timePlaceholder;
  }, [isTimeFocused, timeDisplayValue, hourFormat, timePlaceholder]);

  // --- Unified change handler ---
  const fireChange = useCallback(newValue => {
    if (isBusy) {
      return;
    }
    onChange(newValue);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(newValue);
        await changeAction(newValue);
      });
    }
  }, [isBusy, onChange, changeAction, startTransition, setOptimisticValue]);

  // --- Popover ---
  const popover = usePopover({
    dialogLabel: t('@astryx.dateTimeInput.dialogLabel'),
    closeButtonLabel: t('@astryx.dateInput.closeCalendar'),
    onHide: () => dateInputRef.current?.focus()
  });
  const handleCalendarToggle = useCallback(() => {
    if (!isEffectivelyDisabled) {
      if (popover.isOpen) {
        popover.hide();
      } else {
        popover.show();
      }
    }
  }, [isEffectivelyDisabled, popover]);
  const handleDateInputClick = useCallback(() => {
    if (!isEffectivelyDisabled && !popover.isOpen) {
      popover.show({
        skipAutoFocus: true
      });
    }
  }, [isEffectivelyDisabled, popover]);

  // --- Date handlers ---
  const handleDateChange = useCallback((newDate, source) => {
    const currentTime = valueParts.time ?? getDefaultTime(hasSeconds);
    let effectiveTime = currentTime;
    if (minParts.date && newDate === minParts.date && minParts.time) {
      if (!isTimeInRange(effectiveTime, minParts.time, undefined)) {
        effectiveTime = minParts.time;
      }
    }
    if (maxParts.date && newDate === maxParts.date && maxParts.time) {
      if (!isTimeInRange(effectiveTime, undefined, maxParts.time)) {
        effectiveTime = maxParts.time;
      }
    }
    const combined = combineDateTime(newDate, effectiveTime);
    if (combined) {
      fireChange(combined);
    }
    if (source === 'calendar') {
      setDatePendingInput(null);
      popover.hide();
    }
  }, [valueParts.time, hasSeconds, minParts, maxParts, fireChange, popover]);
  const handleDateInputChange = useCallback(e => {
    // With a disabledMessage the input drops `disabled` for focusability, so
    // guard value mutation explicitly (readOnly also blocks typing).
    if (isEffectivelyDisabled) {
      return;
    }
    const text = e.target.value;
    setDatePendingInput(text);
    const parsed = parseDateInput(text);
    if (parsed && plainDateToISO(parsed) !== valueParts.date && !isDateDisabled(parsed)) {
      const parsedISO = plainDateToISO(parsed);
      lastFiredDateRef.current = parsedISO;
      handleDateChange(parsedISO, 'input');
      calendarRef.current?.navigateTo(parsedISO);
    }
  }, [valueParts.date, isDateDisabled, handleDateChange, isEffectivelyDisabled]);
  const commitDatePendingInput = useCallback(() => {
    if (datePendingInput === null) {
      return;
    }
    if (!datePendingInput.trim()) {
      if (value !== undefined) {
        fireChange(undefined);
      }
      setDatePendingInput(null);
      return;
    }
    const parsed = parseDateInput(datePendingInput);
    if (parsed && !isDateDisabled(parsed)) {
      const parsedISO = plainDateToISO(parsed);
      if (parsedISO !== valueParts.date) {
        handleDateChange(parsedISO, 'input');
      }
    }
    setDatePendingInput(null);
  }, [datePendingInput, value, valueParts.date, fireChange, isDateDisabled, handleDateChange]);
  const handleDateBlur = useCallback(() => {
    commitDatePendingInput();
  }, [commitDatePendingInput]);
  const handleDateKeyDown = useCallback(e => {
    if (e.key === 'Escape' && popover.isOpen) {
      e.preventDefault();
      popover.hide();
    } else if ((e.key === 'ArrowDown' || e.altKey && e.key === 'ArrowDown') && !popover.isOpen) {
      // APG combobox: ArrowDown (and Alt+ArrowDown) opens the calendar
      // popover from the keyboard, keeping focus in the input (forms-13).
      e.preventDefault();
      if (!isEffectivelyDisabled) {
        popover.show({
          skipAutoFocus: true
        });
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitDatePendingInput();
    }
  }, [popover, commitDatePendingInput, isEffectivelyDisabled]);

  // --- Time handlers ---
  const handleTimeInputChange = useCallback(e => {
    // With a disabledMessage the input drops `disabled` for focusability, so
    // guard value mutation explicitly (readOnly also blocks typing).
    if (isEffectivelyDisabled) {
      return;
    }
    const text = e.target.value;
    setTimePendingInput(text);
    const parsed = parseTimeInput(text, hasSeconds);
    if (parsed && isTimeInRange(parsed, timeMin, timeMax) && parsed !== valueParts.time) {
      if (valueParts.date) {
        const combined = combineDateTime(valueParts.date, parsed);
        if (combined) {
          fireChange(combined);
        }
      }
    }
  }, [hasSeconds, timeMin, timeMax, valueParts.time, valueParts.date, fireChange, isEffectivelyDisabled]);
  const handleTimeFocus = useCallback(() => {
    // A disabled/busy input stays focusable (via aria-disabled) so its reason
    // is discoverable, but it must not present editing affordances — keep the
    // static placeholder rather than swapping in the format hint.
    if (isEffectivelyDisabled) {
      return;
    }
    setIsTimeFocused(true);
  }, [isEffectivelyDisabled]);
  const handleTimeBlur = useCallback(() => {
    setIsTimeFocused(false);
    if (timePendingInput === null) {
      return;
    }
    if (!timePendingInput.trim()) {
      // Empty time: revert display to previous value (don't emit partial datetime)
      setTimePendingInput(null);
      return;
    }
    const parsed = parseTimeInput(timePendingInput, hasSeconds);
    if (parsed && isTimeInRange(parsed, timeMin, timeMax)) {
      if (parsed !== valueParts.time && valueParts.date) {
        const combined = combineDateTime(valueParts.date, parsed);
        if (combined) {
          fireChange(combined);
        }
      }
    }
    setTimePendingInput(null);
  }, [timePendingInput, hasSeconds, timeMin, timeMax, valueParts, fireChange]);
  const handleTimeKeyDown = useCallback(e => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      let currentTime = valueParts.time;
      if (!currentTime) {
        const now = new Date();
        currentTime = formatISOTime({
          hour: now.getHours(),
          minute: now.getMinutes(),
          second: now.getSeconds()
        }, hasSeconds);
      }
      const delta = e.key === 'ArrowUp' ? timeIncrement : -timeIncrement;
      const newTime = adjustTime(currentTime, delta, hasSeconds);
      if (isTimeInRange(newTime, timeMin, timeMax) && valueParts.date) {
        const combined = combineDateTime(valueParts.date, newTime);
        if (combined) {
          fireChange(combined);
        }
      }
    }
  }, [valueParts, hasSeconds, timeIncrement, timeMin, timeMax, fireChange]);

  // --- Clear ---
  const handleClear = useCallback(() => {
    fireChange(undefined);
    dateInputRef.current?.focus();
  }, [fireChange]);

  // Focus time input when clicking wrapper padding/icon
  const {
    onClick: handleTimeWrapperClick,
    onMouseUp: handleTimeWrapperMouseUp
  } = useInputContainer({
    containerRef: timeContainerRef,
    inputRef: timeInputRef,
    disabled: isEffectivelyDisabled
  });
  return /*#__PURE__*/_jsxs(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: dateInputId,
    descriptionID: description ? descriptionID : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    labelTooltip: labelTooltip,
    statusVariant: "detached",
    width: width,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: disabledMessageTooltip.ref,
      ...rest,
      ...mergeProps(themeProps('date-time-input', {
        size,
        status: status?.type ?? null,
        disabled: isDisabled ? 'disabled' : null
      }), stylex.props(styles.row, xstyle), className, style),
      children: [/*#__PURE__*/_jsxs("div", {
        ref: popover.triggerRef,
        ...mergeProps(themeProps('date-time-input-date-segment', {
          size,
          status: status?.type ?? null
        }), stylex.props(inputWrapperStyles.base, sizeStyles[size], styles.dateWrapper, isEffectivelyDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && !isEffectivelyDisabled && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type])),
        children: [/*#__PURE__*/_jsx("button", {
          type: "button",
          onClick: handleCalendarToggle,
          disabled: isEffectivelyDisabled,
          "aria-label": popover.isOpen ? t('@astryx.dateInput.toggleCalendarClose') : t('@astryx.dateInput.openCalendar'),
          ...{
            0: {
              className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
            },
            1: {
              className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w xh6dtrn x1a2a7pz x1p25gnr x1y3gkto x1h6gzvc"
            }
          }[!!isEffectivelyDisabled << 0],
          children: /*#__PURE__*/_jsx(Icon, {
            icon: "calendar",
            size: "sm",
            color: "secondary"
          })
        }), /*#__PURE__*/_jsx("input", {
          ref: mergeRefs(ref, dateInputRef),
          id: dateInputId,
          type: "text",
          role: "combobox",
          value: dateDisplayValue,
          onChange: handleDateInputChange,
          onBlur: handleDateBlur,
          onClick: handleDateInputClick,
          onKeyDown: handleDateKeyDown,
          placeholder: placeholder
          // With a disabledMessage the input keeps focusability via
          // aria-disabled so the reason is focus-discoverable; typing is
          // blocked with readOnly and the mutation guards, and calendar
          // activation is blocked by the isEffectivelyDisabled guards.
          ,
          disabled: isEffectivelyDisabled && !showsDisabledMessage,
          "aria-disabled": showsDisabledMessage ? 'true' : undefined,
          readOnly: showsDisabledMessage || undefined,
          "aria-describedby": ariaDescribedBy,
          "aria-required": isRequired === true ? 'true' : undefined,
          "aria-invalid": status?.type === 'error' || !isDateInputValid ? 'true' : undefined,
          "aria-busy": isBusy || undefined,
          "aria-expanded": popover.isOpen,
          "aria-haspopup": "dialog",
          "aria-controls": popover.isOpen ? popover.id : undefined,
          "aria-autocomplete": "none",
          autoComplete: "off",
          ...{
            0: {
              className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5"
            },
            2: {
              className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc"
            },
            1: {
              className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 xv1l7n4"
            },
            3: {
              className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc xv1l7n4"
            }
          }[!!isEffectivelyDisabled << 1 | !!!isDateInputValid << 0]
        }), /*#__PURE__*/_jsx(VisuallyHidden, {
          as: "div",
          role: "alert",
          "aria-live": "assertive",
          children: !isDateInputValid ? 'Invalid date' : ''
        }), hasClear && value !== undefined && !isEffectivelyDisabled && /*#__PURE__*/_jsx("button", {
          type: "button",
          onClick: handleClear,
          "aria-label": t('@astryx.dateInput.clear', {
            label
          }),
          ...{
            className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
          },
          children: /*#__PURE__*/_jsx(Icon, {
            icon: "close",
            size: "sm",
            color: "secondary"
          })
        }), isBusy && /*#__PURE__*/_jsx(Spinner, {
          size: "sm"
        }), statusIcon]
      }), /*#__PURE__*/_jsxs("div", {
        ref: timeContainerRef,
        onClick: handleTimeWrapperClick,
        onMouseUp: handleTimeWrapperMouseUp,
        ...mergeProps(themeProps('date-time-input-time-segment', {
          size,
          status: status?.type ?? null
        }), stylex.props(inputWrapperStyles.base, sizeStyles[size], styles.timeWrapper, isEffectivelyDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && !isEffectivelyDisabled && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type])),
        children: [/*#__PURE__*/_jsx("div", {
          ...{
            className: "x78zum5 x6s0dn4 xl56j7k x2lah0s"
          },
          children: /*#__PURE__*/_jsx(Icon, {
            icon: "clock",
            size: "sm",
            color: "secondary"
          })
        }), /*#__PURE__*/_jsx("input", {
          ref: timeInputRef,
          id: timeInputId,
          type: "text",
          value: timeDisplayValue,
          onChange: handleTimeInputChange,
          onFocus: handleTimeFocus,
          onBlur: handleTimeBlur,
          onKeyDown: handleTimeKeyDown,
          placeholder: resolvedTimePlaceholder
          // With a disabledMessage the input keeps focusability via
          // aria-disabled so the reason is focus-discoverable; typing is
          // blocked with readOnly and the mutation guards.
          ,
          disabled: isEffectivelyDisabled && !showsDisabledMessage,
          "aria-disabled": showsDisabledMessage ? 'true' : undefined,
          readOnly: showsDisabledMessage || undefined,
          "aria-label": timeLabel ?? t('@astryx.dateTimeInput.timeSuffix', {
            label
          }),
          "aria-describedby": ariaDescribedBy,
          "aria-required": isRequired === true ? 'true' : undefined,
          "aria-invalid": status?.type === 'error' || !isTimeInputValid ? 'true' : undefined,
          "aria-busy": isBusy || undefined,
          ...{
            0: {
              className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5"
            },
            2: {
              className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc"
            },
            1: {
              className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 xv1l7n4"
            },
            3: {
              className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc xv1l7n4"
            }
          }[!!isEffectivelyDisabled << 1 | !!!isTimeInputValid << 0]
        }), /*#__PURE__*/_jsx(VisuallyHidden, {
          as: "div",
          role: "alert",
          "aria-live": "assertive",
          children: !isTimeInputValid ? 'Invalid time' : ''
        })]
      })]
    }), popover.render(/*#__PURE__*/_jsx(Calendar, {
      handleRef: calendarRef,
      mode: "single",
      value: valueParts.date,
      onChange: d => handleDateChange(d, 'calendar'),
      min: calendarMin,
      max: calendarMax,
      dateConstraints: dateConstraints,
      numberOfMonths: numberOfMonths,
      weekStartsOn: weekStartsOn
    }), {
      placement: 'below',
      alignment: 'start'
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
DateTimeInput.displayName = 'DateTimeInput';